import { CurrencyPipe, DatePipe, DecimalPipe, I18nPluralPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { LoadingContainerComponent } from 'common-ui';
import { ItemPriceComponentStore, defaultItemPricingModel } from 'products-data';
import { CommonItemComponentBase } from '../../CommonItemComponentBase';
import { CallForPriceButtonComponent } from '../call-for-price-button';
import { ItemPrice, PromotionItemPrice, QuotedItemPrice } from '@idealsupply/products-api-angular-client';

@Component({
  selector: 'ideal-product-price',
  imports: [
    LoadingContainerComponent,
    CallForPriceButtonComponent,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    RouterModule,
    CurrencyPipe,
    DecimalPipe,
    I18nPluralPipe,
    DatePipe,
  ],
  standalone: true,
  templateUrl: './product-price.component.html',
  styleUrl: './product-price.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ItemPriceComponentStore],
  host: {
    '[class.sale]': 'isSale()',
    '[class.limited-sale]': 'isSaleLimited()',
    '[style.--digit-size-multiplier]': 'digitSizeMultiplier()',
  },
})
export class ProductPriceComponent extends CommonItemComponentBase {
  protected readonly priceTestFactor = 1.0;
  protected readonly priceContainerTestFactor = 1.0;

  private readonly _priceStore = inject(ItemPriceComponentStore);
  protected readonly pricingLoading = this._priceStore.itemPricing.loading;
  // protected readonly pricingError = this._priceStore.itemPricing.error;
  // protected readonly pricingSuccess = this._priceStore.itemPricing.success;
  private readonly pricing = computed(() => this._priceStore.itemPricing()?.pricing ?? defaultItemPricingModel);

  protected readonly callForPricing = computed(() => this.pricing().isCallForPricing);
  protected readonly priceNormalized = computed(() => this.pricing().pricePerSoldIn);
  protected readonly unitLabel = computed(() => this.pricing().unitLabelLocalized);
  protected readonly isSale = computed(() => this.pricing().isOnSale);
  protected readonly isSaleLimited = computed(() => this.pricing().isOnSaleLimited);
  protected readonly hasPrice = computed(() => this.pricing().hasPrice);
  protected readonly sale = computed(() => this.pricing().sale);

  protected readonly mainPrice = computed(() => ({
    priceBase: this.pricing().pricePerSoldInRegular,
    price: this.pricing().pricePerSoldIn,
    pricePer: this.pricing().quantitySoldIn,
    isSale: this.pricing().isOnSale,
  }));
  protected readonly containerPrice = computed(() => ({
    priceBase: this.pricing().pricePerContainerRegular,
    price: this.pricing().pricePerContainer,
    pricePer: this.pricing().quantityPerContainer,
    isSale: this.pricing().isOnSale,
    unit: this.pricing().unitContainerLabelLocalized,
  }));

  protected readonly hasContainerPrice = computed(() => this.pricing().hasContainer);
  protected readonly digitSizeMultiplier = computed(() => {
    if (!this.hasContainerPrice()) {
      return 1;
    }
    const containerPrice = this.containerPrice().price * this.priceContainerTestFactor;
    const mainPrice = this.mainPrice().price * this.priceTestFactor;

    const len = mainPrice.toFixed(2).length + containerPrice.toFixed(2).length;

    if (len <= 17) {
      return 1;
    } else if (len <= 19) {
      return 0.9;
    } else if (len <= 21) {
      return 0.8;
    } else if (len <= 25) {
      return 0.7;
    }
    return 0.6;
  });

  constructor() {
    super();
    effect(() => this._priceStore.selectItemById(this.itemId()));
  }

  protected $promo(price: ItemPrice): PromotionItemPrice {
    return price as PromotionItemPrice;
  }

  protected $quote(price: ItemPrice): QuotedItemPrice {
    return price as QuotedItemPrice;
  }
}
